import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import SidebarOrganizationDropdown from './SidebarOrganizationDropdown';

// Logo imports
import logodark from '../../assets/images/web-logo-01 (1).png';
import logosm from '../../assets/images/web-logo-01 (1).png';

const Sidebar = ({ mobileOpen = false, onNavigate, onCloseMobile }) => {
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const canManageOrgUsers = Boolean(
    user?.is_organization_admin ?? user?.is_organization_owner
  );
  const [collapsed, setCollapsed] = React.useState(false);
  const [expandedMenus, setExpandedMenus] = React.useState({
    envelopes: location.pathname.startsWith('/envelopes'),
    templates: false,
    settings: location.pathname.startsWith('/settings')
  });

  // Auto-collapse on envelope/template editor; restore when leaving so nav stays usable.
  React.useEffect(() => {
    const onEditorRoute =
      location.pathname.startsWith('/create-envelope')
      || location.pathname.startsWith('/create-template');
    setCollapsed(onEditorRoute);
  }, [location.pathname]);

  // Update expanded state based on current path
  React.useEffect(() => {
    if (location.pathname.startsWith('/envelopes')) {
      setExpandedMenus(prev => ({ ...prev, envelopes: true }));
    }
    if (location.pathname.startsWith('/settings')) {
      setExpandedMenus(prev => ({ ...prev, settings: true }));
    }
  }, [location.pathname]);

  const toggleMenu = (menuKey) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  const getMenuKey = (path) => {
    return path.replace('/', '').replace('-', '_');
  };

  const handleNavClick = () => {
    if (typeof onNavigate === 'function') onNavigate();
  };

  const menuItems = [
    { path: '/dashboard', label: 'Home', icon: 'ri-home-line' },
    ...(canManageOrgUsers
      ? [{ path: '/team-dashboard', label: 'Team dashboard', icon: 'ri-dashboard-line' }]
      : []),
    // { path: '/documents', label: 'Documents', icon: 'ri-file-line' },
    { 
      path: '/envelopes', 
      label: 'Envelopes', 
      icon: 'ri-mail-line', 
      hasDropdown: true,
      subItems: [
        { path: '/envelopes/sent', label: 'Sent', icon: 'ri-send-plane-line' },
        { path: '/envelopes/completed', label: 'Completed', icon: 'ri-checkbox-circle-line' },
        { path: '/envelopes/revoked', label: 'Revoked', icon: 'ri-close-circle-line' },
        { path: '/envelopes/drafts', label: 'Drafts', icon: 'ri-draft-line' },
      ]
    },
    { path: '/templates', label: 'Templates', icon: 'ri-file-list-3-line' },
    ...(canManageOrgUsers
      ? [{
        path: '/settings',
        label: 'User Management',
        icon: 'ri-team-line',
        hasDropdown: true,
        subItems: [
          { path: '/settings/users', label: 'Users', icon: 'ri-user-line' },
          { path: '/settings/roles', label: 'Roles', icon: 'ri-shield-user-line' },
        ],
      }]
      : []),
    // { path: '/contacts', label: 'Contacts', icon: 'ri-user-line' },
  ];

  // const secondaryItems = [
  //   { path: '/catalog', label: 'Catalog', icon: 'ri-shopping-bag-line' },
  //   { path: '/forms', label: 'Forms', icon: 'ri-file-text-line' },
  //   { path: '/reports', label: 'Reports', icon: 'ri-bar-chart-line' },
  //   { path: '/extensions', label: 'Extensions', icon: 'ri-puzzle-line' },
  // ];

  // const bottomItems = [
  //   { path: '/discover', label: 'Discover', icon: 'ri-graduation-cap-line' },
  //   { path: '/invite', label: 'Invite users', icon: 'ri-user-add-line' },
  //   { path: '/settings', label: 'Settings', icon: 'ri-settings-3-line', hasMore: true },
  // ];

  return (
    <div className={`sidebar${collapsed ? ' collapsed' : ''}${mobileOpen ? ' open' : ''}`}>
      {/* Logo Section */}
      <div className="sidebar-header">
        <div className="d-flex align-items-center justify-content-between w-100">
          <div className="d-flex align-items-center">
            <Link to="/dashboard" className="sidebar-logo" onClick={handleNavClick}>
              <img src={logodark} alt="Volksign" height="90" className="logo-lg" />
              <img src={logosm} alt="Volksign" height="40" className="logo-sm" />
            </Link>
          </div>
          <button
            type="button"
            className="sidebar-mobile-close btn btn-link p-0 text-muted"
            onClick={onCloseMobile}
            aria-label="Close navigation menu"
          >
            <i className="ri-close-line" style={{ fontSize: '1.5rem' }}></i>
          </button>
        </div>
      </div>

      <SidebarOrganizationDropdown collapsed={collapsed} />

      {/* Create Button */}
      <div className="sidebar-create-btn px-3 mb-3">
        <Link
          to="/create-envelope"
          className="btn btn-primary w-100 d-flex align-items-center justify-content-center text-decoration-none"
          onClick={handleNavClick}
        >
          <i className="ri-add-line me-2"></i>
          <span>Create envelope</span>
        </Link>
      </div>

      {/* Main Navigation */}
      <nav className="sidebar-nav">
        <div className="sidebar-nav-content">
          <ul className="list-unstyled mb-0">
            {menuItems.map((item) => {
              const menuKey = getMenuKey(item.path);
              const isExpanded = expandedMenus[menuKey] || false;
              const isActive = location.pathname.startsWith(item.path);
              
              return (
                <li key={item.path}>
                  {item.hasDropdown && item.subItems ? (
                    <>
                      <div
                        className={`sidebar-link ${isActive ? 'active' : ''}`}
                        onClick={() => toggleMenu(menuKey)}
                        style={{ cursor: 'pointer' }}
                      >
                        <i className={item.icon}></i>
                        <span>{item.label}</span>
                        <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line ms-auto`}></i>
                      </div>
                      <ul className={`sidebar-submenu list-unstyled mb-0 ${isExpanded ? 'expanded' : ''}`}>
                        {item.subItems.map((subItem) => (
                          <li key={subItem.path}>
                            <Link
                              to={subItem.path}
                              className={`sidebar-link sidebar-submenu-link ${location.pathname === subItem.path ? 'active' : ''}`}
                              onClick={handleNavClick}
                            >
                              <i className={subItem.icon}></i>
                              <span>{subItem.label}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <Link
                      to={item.path}
                      className={`sidebar-link ${location.pathname === item.path ? 'active' : ''}`}
                      onClick={handleNavClick}
                    >
                      <i className={item.icon}></i>
                      <span>{item.label}</span>
                      {item.hasDropdown && <i className="ri-arrow-down-s-line ms-auto"></i>}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>

          {/* Secondary Navigation */}
          <div className="sidebar-section-divider"></div>
          {/* <ul className="list-unstyled mb-0">
            {secondaryItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`sidebar-link ${location.pathname === item.path ? 'active' : ''}`}
                >
                  <i className={item.icon}></i>
                  <span>{item.label}</span>
                </Link>
              </li>
            ))}
          </ul> */}
        </div>

        {/* Bottom Navigation */}
        {/* <div className="sidebar-bottom">
          <ul className="list-unstyled mb-0">
            {bottomItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`sidebar-link ${location.pathname === item.path ? 'active' : ''}`}
                >
                  <i className={item.icon}></i>
                  <span>{item.label}</span>
                  {item.hasMore && <i className="ri-more-line ms-auto"></i>}
                </Link>
              </li>
            ))}
          </ul>
        </div> */}
      </nav>
    </div>
  );
};

export default Sidebar;
